import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Stack,
  Divider,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  BedOutlined,
  BathtubOutlined,
  SquareFootOutlined,
  LocationOn,
  ApartmentOutlined,
  HouseOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

export type PropertyType = {
  id: string;
  title: string;
  address: string;
  price: number;
  type: 'house' | 'apartment' | 'condo' | 'townhouse';
  status: 'for sale' | 'for rent' | 'sold' | 'pending';
  bedrooms: number;
  bathrooms: number;
  area: number;
  description: string;
  images: string[];
  isFeatured?: boolean;
  isNew?: boolean;
  agentId?: number; // <-- ADD THIS LINE
};

type PropertyCardProps = {
  property: PropertyType;
  variant?: 'standard' | 'horizontal' | 'featured';
};

const PropertyCard = ({ property, variant = 'standard' }: PropertyCardProps) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [isFavorite, setIsFavorite] = useState(false);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorite(!isFavorite);
    enqueueSnackbar(
      isFavorite 
        ? 'Property removed from favorites' 
        : 'Property added to favorites',
      { 
        variant: isFavorite ? 'info' : 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
      }
    );
  };

  const isHorizontal = variant === 'horizontal';
  const isFeatured = variant === 'featured';

  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(property.price);

  // Function to truncate description
  const truncateDescription = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  // Property icon based on type
  const PropertyIcon = property.type === 'house' ? HouseOutlined : ApartmentOutlined;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: isHorizontal ? 'row' : 'column',
        position: 'relative',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
        },
        overflow: 'visible',
      }}
    >
      {/* Status Chip */}
      {property.status && (
        <Chip
          label={property.status}
          color={
            property.status === 'for sale' ? 'primary' :
            property.status === 'for rent' ? 'secondary' :
            property.status === 'pending' ? 'warning' : 'default'
          }
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 1,
            textTransform: 'capitalize',
          }}
        />
      )}

      {/* New Tag */}
      {property.isNew && (
        <Chip
          label="NEW"
          color="error"
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1,
          }}
        />
      )}

      {/* Favorite Button */}
      <IconButton
        aria-label={isFavorite ? 'remove from favorites' : 'add to favorites'}
        onClick={handleFavoriteClick}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          bgcolor: theme.palette.background.paper,
          opacity: 0.9,
          '&:hover': {
            bgcolor: theme.palette.background.paper,
            opacity: 1,
          },
        }}
      >
        {isFavorite ? <Favorite color="error" /> : <FavoriteBorder />}
      </IconButton>

      <CardActionArea component={RouterLink} to={`/property/${property.id}`}>
        {/* Property Image */}
        <CardMedia
          component="img"
          image={property.images[0]}
          alt={property.title}
          sx={{
            height: isHorizontal ? 220 : 200,
            width: isHorizontal ? 280 : '100%',
            objectFit: 'cover',
          }}
        />

        {/* Property Information */}
        <CardContent 
          sx={{ 
            flex: 1,
            p: isHorizontal ? 2 : undefined,
          }}
        >
          {/* Price */}
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div" fontWeight={700} color="primary">
              {formattedPrice}
              {property.status === 'for rent' && (
                <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                  /month
                </Typography>
              )}
            </Typography>
            <Tooltip title={property.type} arrow>
              <PropertyIcon color="action" />
            </Tooltip>
          </Box>

          {/* Title */}
          <Typography
            variant="subtitle1"
            component="h2"
            gutterBottom
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {property.title}
          </Typography>

          {/* Address */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
            <LocationOn fontSize="small" color="action" sx={{ mr: 0.5, mt: 0.25 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {property.address}
            </Typography>
          </Box>

          {/* Description - only for larger variants */}
          {isFeatured && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                mb: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {truncateDescription(property.description, 120)}
            </Typography>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* Property Features */}
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-around"
            alignItems="center"
            sx={{ mt: 1 }}
          >
            <Tooltip title="Bedrooms" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BedOutlined fontSize="small" color="action" sx={{ mr: 0.5 }} />
                <Typography variant="body2">{property.bedrooms}</Typography>
              </Box>
            </Tooltip>

            <Tooltip title="Bathrooms" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BathtubOutlined fontSize="small" color="action" sx={{ mr: 0.5 }} />
                <Typography variant="body2">{property.bathrooms}</Typography>
              </Box>
            </Tooltip>

            <Tooltip title="Square Feet" arrow>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SquareFootOutlined fontSize="small" color="action" sx={{ mr: 0.5 }} />
                <Typography variant="body2">{property.area} ft²</Typography>
              </Box>
            </Tooltip>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default PropertyCard;